import React, { useState } from 'react';
import {
  Modal,
  Input,
  message,
  Tooltip,
  Typography,
  Space,
  Form,
  Divider,
} from 'antd';
import {
  ExclamationCircleOutlined,
    
    CloseCircleOutlined
} from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '../services/httpClient';
import { useTranslation } from 'react-i18next';
import Button from 'antd/lib/button';
import { hasPermission } from '../services/permissionService';
import { getPermissionKeyFromApi } from './fromatPermission';

interface RecordType {
  id: number | string;
  code?: string;
  policyHolderName?: string;
  planName?: string;
    category?: string;
    name?: string;
    policyCode?: string; // Made optional
}

interface PolicyCancelModalProps {
  record: RecordType;
    queryKey?: string | unknown[];
    queryKeySecond?: string | unknown[];
  icon?: boolean;
  successMessage?: string;
  apiUrl: string;
  title?: string;
    confirmButtonText?: string;
    payloadKeys?: unknown[];
}
interface ApiResponse<T = unknown> {
  data: T | null;
  paging: unknown;
  statusCode: number;
  isError: boolean;
  title?: string;
  developerMessage?: string;
  message?: string;
  messageArgs?: unknown;
}

const PolicyCancelModal: React.FC<PolicyCancelModalProps> = ({
  record,
    queryKey,
    queryKeySecond,
    icon = <CloseCircleOutlined />,
  successMessage,
  apiUrl,
  title = 'Cancel Policy',
    confirmButtonText,
    payloadKeys
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [form] = Form.useForm();

  const mutation = useMutation<ApiResponse, Error, string>({
    mutationFn: async (cancelledNote) => {
          const response = (await post(apiUrl, {
        id: record.id,
          cancelledNote,
        ids: payloadKeys
      })) as ApiResponse;

      // ✅ Check for custom error
      if (response?.isError) {
        // ✅ Close confirm modal manually
        Modal.destroyAll();

        // ✅ Close form modal
        setIsModalVisible(false);
        form.resetFields();
        throw new Error(response?.title || 'An error occurred');
      }

      return response;
    },
    onSuccess: () => {
      message.success(successMessage || t('Policy cancelled successfully'));
      if (queryKey) {
        queryClient.invalidateQueries({
          queryKey: Array.isArray(queryKey) ? queryKey : [queryKey],
        });
        }

        if (queryKeySecond) {
            queryClient.invalidateQueries({
                queryKey: Array.isArray(queryKeySecond) ? queryKeySecond : [queryKeySecond],
            });
        }
    },
    onError: (err) => {
      message.error(t(err.message) || t('An error occurred'));
    },
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      Modal.confirm({
        title: confirmButtonText || t('Are you sure you want to cancel this policy?'),
        icon: <ExclamationCircleOutlined />,
        okText: t('Yes'),
        okType: 'danger',
        cancelText: t('No'),
        onOk: async () => {
          setConfirmLoading(true);
          try {
            await mutation.mutateAsync(values.cancelledNote);
            setIsModalVisible(false);
            form.resetFields();
          } finally {
            setConfirmLoading(false);
          }
        },
      });
    } catch {
      // validation errors, do nothing
    }
  };

  const openModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsModalVisible(true);
  };

  return (
      <>
          {
              hasPermission(getPermissionKeyFromApi(apiUrl)) && (
                  <>
                      <Tooltip title={t(title)}>
                          <Button size='large' style={{ cursor: 'pointer', color: title.toLowerCase().includes('delete') || title.toLowerCase().includes('cancel') ? 'red' : 'green' }}
                              icon={icon}
                              onClick={openModal}
                          />
                      </Tooltip>

                      <Modal
                          title={t('Cancel Policy')}
                          open={isModalVisible}
                          onOk={handleSubmit}
                          onCancel={() => setIsModalVisible(false)}
                          confirmLoading={confirmLoading}
                          okText={t('Submit')}
                          cancelText={t('Cancel')}
                          maskClosable={false}
                      >
                          <Divider />
                          <Space direction="vertical" style={{ marginBottom: 16 }}>
                              <Typography.Text>
                                  <strong>Code:</strong> {record.code || record.policyCode || '-'}
                              </Typography.Text>
                              <Typography.Text>
                                  <strong>Name:</strong> {record.policyHolderName || record.name || '-'}
                              </Typography.Text>
                              <Typography.Text>
                                  <strong>Plan Name:</strong> {record.planName || '-'}
                              </Typography.Text>
                              <Typography.Text>
                                  <strong>Category:</strong> {record.category || '-'}
                              </Typography.Text>
                          </Space>
                          <Divider />

                          <Form form={form} layout="vertical">
                              <Form.Item
                                  label={t('Cancellation Reason')}
                                  name="cancelledNote"
                                  rules={[
                                      { required: true, message: t('This field is required') },
                                      {
                                          min: 2,
                                          max: 250,
                                          message: t('CancelledNote must be between 2 and 250 characters.'),
                                      },
                                  ]}
                              >
                                  <Input.TextArea rows={4} placeholder={t('Enter cancellation reason')} />
                              </Form.Item>
                          </Form>
                      </Modal>

                  </>
              )
          }
    </>
  );
};

export default PolicyCancelModal;
